/**
 * Explainer page for the "Keep WhatsApp awake" feature, opened from the header
 * pill's "Learn more" link. Explains what it does, why it's needed, and when
 * it's safe to turn off — and lets the user toggle it right here too.
 */
import React from 'react';
import AppView from '@common/components/app-view';
import { Zap, Moon, MessageCircle, CheckCircle2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { selectKeepWhatsappAwake } from '@common-reducers/UserSelectors';
import { setKeepWhatsappAwake } from '@common-reducers/UserThunks';

const KeepWhatsappAwakeView: React.FC = () => {
  const dispatch = useAppDispatch();
  const enabled = useAppSelector(selectKeepWhatsappAwake);

  return (
    <AppView title="Keep WhatsApp Awake" showStatusItems={false}>
      <div className="max-w-2xl mx-auto w-full px-6 py-8 text-[var(--text-color,#1f2937)]">
        {/* Hero */}
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-green-500/15 text-green-600">
            <Zap className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Keep WhatsApp Awake</h1>
            <p className="text-sm text-muted-foreground">
              So Chrome doesn&apos;t put your WhatsApp tab to sleep 😴
            </p>
          </div>
        </div>

        {/* Toggle card */}
        <div className="mt-6 flex items-center justify-between rounded-xl border border-green-500/30 bg-green-500/5 px-4 py-3">
          <div>
            <p className="text-sm font-semibold">Keep my WhatsApp tab awake</p>
            <p className="text-xs text-muted-foreground">
              {enabled ? 'On — your messages keep flowing.' : 'Off — WhatsApp may doze off in the background.'}
            </p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={(checked) => dispatch(setKeepWhatsappAwake(checked))}
            aria-label="Toggle keep WhatsApp awake"
            className="data-[state=checked]:bg-green-500"
          />
        </div>

        {/* What & why */}
        <Section
          icon={<Moon className="w-5 h-5 text-indigo-500" />}
          title="Why does WhatsApp fall asleep?"
        >
          To save your laptop&apos;s battery, Chrome slows down — and sometimes fully
          freezes — tabs that sit in the background. Great for power, not so great
          for WhatsApp Web: when its tab is frozen, the connection to WhatsApp
          stalls, so scheduled messages and incoming replies can get stuck.
        </Section>

        <Section
          icon={<Zap className="w-5 h-5 text-green-600" />}
          title="What we do about it"
        >
          When Chrome puts your WhatsApp tab to sleep, we briefly switch to it —
          for under a second — and then switch right back to whatever you were
          doing, so your scheduled messages keep sending. You&apos;ll see your
          screen jump to WhatsApp for a moment when it happens.
        </Section>

        <Section
          icon={<MessageCircle className="w-5 h-5 text-blue-500" />}
          title="Why it matters"
        >
          Scheduled messages, campaign sends, auto-replies and your live inbox all
          rely on WhatsApp Web staying connected. Keep-awake is what lets Blueticks
          work reliably even when WhatsApp isn&apos;t the tab you&apos;re looking at.
        </Section>

        <Section
          icon={<CheckCircle2 className="w-5 h-5 text-amber-500" />}
          title="When can I turn it off?"
        >
          If you keep WhatsApp Web open and in front of you most of the day, Chrome
          won&apos;t freeze it — so you don&apos;t need the pokes and can switch keep-awake
          off to avoid the occasional flicker. Sending lots of scheduled messages
          while working in other tabs? Leave it on. You can change this anytime
          from the pill in the top bar.
        </Section>
      </div>
    </AppView>
  );
};

const Section: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({
  icon,
  title,
  children,
}) => (
  <div className="mt-6">
    <div className="flex items-center gap-2">
      {icon}
      <h2 className="text-sm font-semibold">{title}</h2>
    </div>
    <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{children}</p>
  </div>
);

export default KeepWhatsappAwakeView;
